import React, { useEffect, useRef } from 'react';
import type { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { SlideInModalPortal } from 'syf-component-library/ui/patterns/SlideInModalPortal';
import { Body } from 'syf-component-library/ui/typography';
import { Button } from 'syf-component-library/ui/atoms';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons/faArrowsRotate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch } from '../../store/store';
import { CIPHER_ACCOUNT_ID } from '../../utils/constants/apiConstants';
import {
  getAccountDetailsContent,
  getCreditLimitIncreaseContent,
} from '../../store/contentSlice';
import {
  getCliAccountId,
  getCreditLimitDesiredAmount,
  getCreditLimitNetIncome,
  submitCreditLimitRequest,
  setCreditDetails,
  resetCliState,
  getFailureResponseStatus,
  getSuccessResponseStatus,
  getCliLoadingStatus,
  getAreCliValuesPopulated,
  getCliModalOpenStatus,
} from '../../store/creditLimitIncreaseSlice';
import { renderAccountDetails } from '../../store/accountListSlice';
import { setImagePath, detectDeviceType } from '../../utils/helpers';
import {
  captureClickAnalyticsObject,
  capturePageLoadAnalytics,
} from '../../utils/analytics/analyticsDispatchers';
import { CREDIT_INCREASE_ERROR } from '../../utils/analytics/analyticsEvents';
import { AppState } from '../../interfaces/store.interface';
import CreditLimitIncreaseRequest from './CreditLimitIncreaseRequest';
import CreditLimitIncreaseResult from './CreditLimitIncreaseResult';
import './creditLimitIncrease.css';

const CreditLimitIncrease: FunctionComponent = (props: any) => {
  const modalStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  } as React.CSSProperties;
  const { account } = props;
  const {
    submitButtonText,
    cancelButtonText,
    creditLimitText,
    closeButtonText,
    submissionErrorText,
  }: any = useSelector(getCreditLimitIncreaseContent);
  const { portfolioLogoAltText }: any = useSelector(getAccountDetailsContent);
  const cliAccountId = useSelector(getCliAccountId);
  const isCliModalOpen = useSelector((state: AppState) =>
    getCliModalOpenStatus(state, account.accountId),
  );
  const dispatch = useAppDispatch();
  const cliDesiredLimit = useSelector(getCreditLimitDesiredAmount);
  const cliAnnualNetIncome = useSelector(getCreditLimitNetIncome);
  const isResultReady = useSelector(getSuccessResponseStatus);
  const hasProcessingFailed = useSelector(getFailureResponseStatus);
  const isCliLoading = useSelector(getCliLoadingStatus);
  const cliWarning = useRef<HTMLDivElement>(null);
  const areCliValuesPopulated = useSelector(getAreCliValuesPopulated);

  useEffect(() => {
    if (hasProcessingFailed && cliWarning.current) {
      cliWarning.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      capturePageLoadAnalytics(CREDIT_INCREASE_ERROR);
    }
  }, [hasProcessingFailed]);

  const handleSubmit = () => {
    if (areCliValuesPopulated) {
      dispatch(
        setCreditDetails({
          cliAvailableCredit: account.availableCredit,
          cliPreviousCreditLimit: account.creditLimit,
        }),
      );

      dispatch(
        submitCreditLimitRequest({
          accountIdType: 'guid',
          [CIPHER_ACCOUNT_ID]: cliAccountId,
          income: parseInt(cliAnnualNetIncome),
          requestedLimit: parseInt(cliDesiredLimit),
        }),
      );
    }
  };
  const closeModal = () => {
    if (!isCliLoading) {
      dispatch(
        renderAccountDetails({
          accountId: cliAccountId,
          renderAccountDetailsDisplayFlag: true,
        }),
      );
      dispatch(resetCliState());
    }
    captureClickAnalyticsObject({
      dataType: detectDeviceType,
      dataReason: `closeCLIpopUp:${account.displayName}`,
      dataObject: 'button',
    });
  };

  let footerGhostButtonClass = '';
  let footerPrimaryButtonHandler = handleSubmit;
  let closeIconClass = 'x-mark allow-pointer';
  let primaryButtonDataChannel = 'Error';
  let cliSubmitButtonDisplayContent = <span>{submitButtonText}</span>;
  let cliComponentToDisplay = <CreditLimitIncreaseRequest {...account} />;

  // handle things when cli request is completed
  if (isResultReady) {
    footerGhostButtonClass = 'cli-ghost-button';
    cliSubmitButtonDisplayContent = <span>{closeButtonText}</span>;
    footerPrimaryButtonHandler = closeModal;
    primaryButtonDataChannel = 'Success';
    cliComponentToDisplay = <CreditLimitIncreaseResult {...account} />;
  }

  // handle things when cli request is pending
  if (isCliLoading) {
    closeIconClass = 'x-mark-loading-state';
    cliSubmitButtonDisplayContent = (
      <FontAwesomeIcon
        icon={faArrowsRotate}
        className="animate-spin arrow-rotate"
        data-testid="cli-loading"
      />
    );
  }

  return (
    isCliModalOpen && (
      <div style={modalStyles}>
        <SlideInModalPortal
          className="cli-container"
          id="slide-in-modal"
          containerId={cliAccountId}
          isOpen={isCliModalOpen}
          escapeModalHandler={closeModal}
          header={
            <div className="header-wrapper">
              <div className="header-section">
                <span className="header-text">{creditLimitText}</span>
                <div tabIndex={0}>
                  <FontAwesomeIcon
                    icon={faXmark}
                    className={closeIconClass}
                    onClick={closeModal}
                    data-testid="close-icon"
                  />
                </div>
              </div>
            </div>
          }
          footer={
            <div className="footer-button-wrapper">
              <div className="footer-button-container">
                <Button
                  buttonType="ghost"
                  data-type={detectDeviceType}
                  data-reason={`cancel:${account.displayName}`}
                  data-object="button"
                  onClick={closeModal}
                  data-test-id="cli-cancel-button"
                  className={footerGhostButtonClass}
                  disabled={isCliLoading}
                  data-testid="cli-ghost-button"
                >
                  {cancelButtonText}
                </Button>
                <Button
                  buttonType="primary"
                  data-type={detectDeviceType}
                  data-reason={`requestIncrease:${account.displayName}`}
                  data-object="button"
                  data-channel={primaryButtonDataChannel}
                  onClick={() => footerPrimaryButtonHandler()}
                  data-testid="cli-primary-button"
                  disabled={isCliLoading || !areCliValuesPopulated}
                >
                  {cliSubmitButtonDisplayContent}
                </Button>
              </div>
            </div>
          }
        >
          <div>
            <Body>
              <div className="cli-internal-header">
                <img
                  src={account.portfolioLogo}
                  alt={`${account.portfolio} ${portfolioLogoAltText}`}
                  className="header-logo"
                />
                <div className="internal-header-x-mark" tabIndex={0}>
                  <FontAwesomeIcon
                    icon={faXmark}
                    className={closeIconClass}
                    data-type={detectDeviceType}
                    data-reason={`closeCLIpopUp:${account.displayName}`}
                    data-object="button"
                    onClick={closeModal}
                    data-testid="close-icon"
                  />
                </div>
              </div>
              <div className="credit-limit-header-divider-wrapper">
                <span className="header-cli-divider"></span>
              </div>
              {cliComponentToDisplay}
            </Body>
          </div>
          {hasProcessingFailed && (
            <div className="cli-warning-section" ref={cliWarning}>
              <div className="cli-warning-block">
                <div className="cli-divider" />
                <div className="cli-warning-content">
                  <div className="cli-circle-exclamation">
                    <img
                      src={setImagePath('circle-exclamation')}
                      alt="exclamation icon"
                    />
                  </div>
                  <span className="cli-warning-text">
                    {submissionErrorText}
                  </span>
                </div>
              </div>
            </div>
          )}
        </SlideInModalPortal>
      </div>
    )
  );
};
export default CreditLimitIncrease;
